import axios from 'axios';
import { parseJwtPayload } from './utils';

const allowedPaths = ['/', '/login', '/register'];

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('auth');
    config.headers.Authorization = `Bearer ${token}`;

    if (config.url && allowedPaths.some((path) => config.url?.includes(path))) {
      return config;
    }

    if (token) {
      const sessionSecret = localStorage.getItem('sessionSecret') || '';
      const queryParams = Object.entries(config.params || {}).map(
        ([key, value]) => ({
          key,
          value,
        })
      );
      const qItems = queryParams.map((p) => {
        const key = String(p.key ?? '');
        const valRaw =
          p.value === undefined || p.value === null ? '' : String(p.value);
        return `${encodeURIComponent(key)}=${encodeURIComponent(valRaw)}`;
      });

      const canonicalQuery = qItems.join('&');
      const method = config.method?.toUpperCase();
      const path = new URL(config.url || '', config.baseURL).pathname;
      const ts = Math.floor(Date.now() / 1000).toString();
      const nonce = crypto.randomUUID();
      const claims = parseJwtPayload(token);
      const userId = claims.userId || claims.sub || claims.uid;
      const payload = `${method}|${path}|${canonicalQuery}|${ts}|${nonce}|${userId}`;
      // console.log(payload);

      const signature = CryptoJS.HmacSHA256(
        payload,
        getKeyWordArray(sessionSecret)
      ).toString(CryptoJS.enc.Hex);

      config.headers['X-Req-Nonce'] = nonce;
      config.headers['X-Req-Ts'] = ts;
      config.headers['X-Req-Sign'] = signature;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) return;
        const res = await axios.post(
          `${process.env.REACT_APP_BASE_URL}auth/refresh`,
          {
            refreshToken,
          }
        );

        const newAccessToken = res.data.data.accessToken;
        const newRefreshToken = res.data.data.refreshToken;
        localStorage.setItem('auth', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError: any) {
        if (
          refreshError.response &&
          refreshError.response.status === 401 &&
          !allowedPaths.includes(window.location.pathname)
        ) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

function looksLikeHex(str: string) {
  return (
    typeof str === 'string' &&
    str.length % 2 === 0 &&
    /^[0-9a-fA-F]+$/.test(str)
  );
}

function getKeyWordArray(sessionSecret: string) {
  return !looksLikeHex(sessionSecret)
    ? CryptoJS.enc.Hex.parse(sessionSecret)
    : CryptoJS.enc.Utf8.parse(sessionSecret);
}

const apiClient = {
  get: (url: string, params?: Record<string, any>, config = {}) =>
    axiosInstance
      .get(url, { params, ...config })
      .then((res) => res.data)
      .catch((error) => {
        if (
          !allowedPaths.includes(window.location.pathname) &&
          error.response &&
          error.response.status === 401
        ) {
          localStorage.removeItem('auth');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
      }),

  post: (url: string, data?: any, config = {}) =>
    axiosInstance
      .post(url, data, config)
      .then((res) => res.data)
      .catch((error) => {
        if (
          !allowedPaths.includes(window.location.pathname) &&
          error.response &&
          error.response.status === 401
        ) {
          localStorage.removeItem('auth');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
      }),

  put: (url: string, data?: any, config = {}) =>
    axiosInstance.put(url, data, config),

  update: (url: string, data?: Object) => {
    return axios.patch(url, data);
  },

  delete: (url: string, config = {}) => axiosInstance.delete(url, config),
};

export default apiClient;
